import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getProfilePicture } from '../utils/profilePicture';
import api from '../services/api';
import { isAdminOrSuperAdmin } from '../utils/auth';
import PlayerSearchDialog from '../components/PlayerSearchDialog';
import '../App.css';
import './TeamManagement.css';
import LoadingSpinner from '../components/LoadingSpinner';

interface Team {
  id: string;
  name: string;
  teamNumber: string;
  logo?: string;
  colors: { primary: string; secondary: string };
  status: string;
  manager: { id: string; firstName: string; lastName: string } | null;
  captain: { id: string; firstName: string; lastName: string } | null;
}

interface Announcement {
  id: string;
  title: string;
  content: string;
  priority: string;
  author: { id: string; firstName: string; lastName: string } | null;
  createdAt: string;
}

interface Message {
  id: string;
  content: string;
  sender: { id: string; firstName: string; lastName: string } | null;
  createdAt: string;
}

interface TrainingSession {
  id: string;
  title: string;
  description?: string;
  scheduledDate: string;
  scheduledTime: string;
  location: string;
  duration?: number;
  rsvpCount: number;
  attendingCount: number;
}

const TeamManagement: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'roster' | 'announcements' | 'messages' | 'training' | 'invitations' | 'payments' | 'guest-history'>('overview');
  const [team, setTeam] = useState<Team | null>(null);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [trainingSessions, setTrainingSessions] = useState<TrainingSession[]>([]);
  const [rosterMembers, setRosterMembers] = useState<Array<{
    id: string;
    role: string;
    position?: string;
    jerseyNumber?: number;
    isGuest: boolean;
    isActive: boolean;
    user: { id: string; firstName: string; lastName: string; email: string } | null;
  }>>([]);
  const [playerStatuses, setPlayerStatuses] = useState<Record<string, {
    canPlay: boolean;
    status: string;
    statusReasons: string[];
    yellowCards: number;
    redCards: number;
    unpaidAmount: number;
  }>>({});
  const [loading, setLoading] = useState(true);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', content: '', priority: 'normal' });
  const [newMessage, setNewMessage] = useState('');
  const [newTraining, setNewTraining] = useState({ title: '', description: '', scheduledDate: '', scheduledTime: '', location: '', duration: '' });
  const [performanceData, setPerformanceData] = useState<any>(null);
  const [matches, setMatches] = useState<any[]>([]);
  const [showGuestPlayerForm, setShowGuestPlayerForm] = useState(false);
  const [guestPlayerForm, setGuestPlayerForm] = useState({ userId: '', matchId: '', position: '', jerseyNumber: '' });
  const [showPlayerSearch, setShowPlayerSearch] = useState(false);
  const [selectedGuestPlayerName, setSelectedGuestPlayerName] = useState('');
  const [invitations, setInvitations] = useState<any[]>([]);
  const [showInvitationForm, setShowInvitationForm] = useState(false);
  const [invitationForm, setInvitationForm] = useState({ email: '', role: 'player', position: '', expiresInDays: '30', maxUses: '' });
  const [teamPayments, setTeamPayments] = useState<any[]>([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ playerId: '', amount: '', description: '', dueDate: '' });
  const [markPaidId, setMarkPaidId] = useState<string | null>(null);
  const [markPaidForm, setMarkPaidForm] = useState({ amountPaid: '', paymentMethod: 'offline', notes: '' });
  const [guestInvites, setGuestInvites] = useState<any[]>([]);
  const [guestHistory, setGuestHistory] = useState<any[]>([]);
  const [showGuestInviteForm, setShowGuestInviteForm] = useState(false);
  const [guestInviteForm, setGuestInviteForm] = useState({ email: '', matchIds: [] as string[], position: '', jerseyNumber: '', expiresInDays: '30', maxUses: '1' });

  useEffect(() => {
    fetchTeamData();
  }, [id, activeTab]);

  const fetchTeamData = async () => {
    try {
      const teamRes = await api.get(`/teams/${id}`);
      setTeam(teamRes.data);

      // Check if user has access to manage this team
      if (teamRes.data.manager?.id !== user?.id && !isAdminOrSuperAdmin(user?.profileType) && teamRes.data.captain?.id !== user?.id) {
        // User doesn't have management access, redirect
        navigate('/teams');
        return;
      }

      if (activeTab === 'announcements') {
        const annRes = await api.get(`/teams/${id}/announcements`);
        setAnnouncements(annRes.data);
      }

      if (activeTab === 'messages') {
        const msgRes = await api.get(`/teams/${id}/messages`);
        setMessages(msgRes.data);
      }

      if (activeTab === 'training') {
        const trainRes = await api.get(`/teams/${id}/training`);
        setTrainingSessions(trainRes.data);
      }

      if (activeTab === 'roster') {
        const rosterRes = await api.get(`/teams/${id}/members`);
        setRosterMembers(rosterRes.data);
        // Fetch matches for guest player selection
        const matchesRes = await api.get('/matches', { params: { teamId: id, page: '1', limit: '100' } });
        const matches = Array.isArray(matchesRes.data) ? matchesRes.data : (matchesRes.data?.matches || []);
        setMatches(matches);
        
        // Fetch player statuses for each member
        const statusPromises = rosterRes.data.map(async (member: any) => {
          try {
            const statusRes = await api.get(`/teams/${id}/members/${member.id}/status`);
            return { memberId: member.id, status: statusRes.data };
          } catch (error) {
            return { memberId: member.id, status: null };
          }
        });
        const statusResults = await Promise.all(statusPromises);
        const statusMap: Record<string, any> = {};
        statusResults.forEach(({ memberId, status }) => {
          if (status) {
            statusMap[memberId] = status;
          }
        });
        setPlayerStatuses(statusMap);
      }

      if (activeTab === 'overview') {
        try {
          const perfRes = await api.get(`/teams/${id}/performance`);
          setPerformanceData(perfRes.data);
        } catch (error) {
          console.error('Error fetching performance data:', error);
        }
      }

      if (activeTab === 'invitations') {
        const invRes = await api.get(`/invitations/teams/${id}/invitations`);
        setInvitations(invRes.data);
        // Also fetch guest invites
        const guestInvRes = await api.get(`/guest-invites/teams/${id}/invites`);
        setGuestInvites(Array.isArray(guestInvRes.data) ? guestInvRes.data : []);
        // Fetch matches for guest invite match selection
        if (matches.length === 0) {
          const matchesRes = await api.get('/matches', { params: { teamId: id, page: '1', limit: '100' } });
          const matchList = Array.isArray(matchesRes.data) ? matchesRes.data : (matchesRes.data?.matches || []);
          setMatches(matchList);
        }
      }

      if (activeTab === 'guest-history') {
        const histRes = await api.get(`/guest-invites/teams/${id}/history`);
        setGuestHistory(histRes.data.data || []);
      }

      if (activeTab === 'payments') {
        const payRes = await api.get(`/payments/team/${id}`);
        setTeamPayments(payRes.data.payments || []);
        // Also fetch roster for the create invoice form
        if (rosterMembers.length === 0) {
          const rosterRes = await api.get(`/teams/${id}/members`);
          setRosterMembers(rosterRes.data);
        }
      }
    } catch (error) {
      console.error('Error fetching team data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!window.confirm('Are you sure you want to remove this player from the team?')) {
      return;
    }
    try {
      await api.delete(`/teams/${id}/members/${memberId}`);
      fetchTeamData();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to remove player');
    }
  };

  const handleUpdateRole = async (memberId: string, role: string, position?: string) => {
    try {
      await api.patch(`/teams/${id}/members/${memberId}/role`, { role, position });
      fetchTeamData();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update role');
    }
  };

  const handleToggleActive = async (memberId: string, currentStatus: boolean) => {
    try {
      await api.patch(`/teams/${id}/members/${memberId}/active`, { isActive: !currentStatus });
      fetchTeamData();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update player status');
    }
  };

  const handleAddGuestPlayer = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/teams/${id}/members/guest`, {
        userId: guestPlayerForm.userId,
        matchId: guestPlayerForm.matchId,
        position: guestPlayerForm.position || undefined,
        jerseyNumber: guestPlayerForm.jerseyNumber ? parseInt(guestPlayerForm.jerseyNumber) : undefined
      });
      setGuestPlayerForm({ userId: '', matchId: '', position: '', jerseyNumber: '' });
      setSelectedGuestPlayerName('');
      setShowGuestPlayerForm(false);
      fetchTeamData();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to add guest player');
    }
  };

  const handleCreateAnnouncement = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/teams/${id}/announcements`, newAnnouncement);
      setNewAnnouncement({ title: '', content: '', priority: 'normal' });
      fetchTeamData();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to create announcement');
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    try {
      await api.post(`/teams/${id}/messages`, { content: newMessage });
      setNewMessage('');
      fetchTeamData();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to send message');
    }
  };

  const handleCreateTraining = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/teams/${id}/training`, {
        ...newTraining,
        duration: newTraining.duration ? parseInt(newTraining.duration) : undefined
      });
      setNewTraining({ title: '', description: '', scheduledDate: '', scheduledTime: '', location: '', duration: '' });
      fetchTeamData();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to create training session');
    }
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!team) {
    return <div className="container">Team not found</div>;
  }

  const isManager = team.manager?.id === user?.id;
  const isAdmin = isAdminOrSuperAdmin(user?.profileType);
  const isCaptain = team.captain?.id === user?.id;
  const canManage = isManager || isAdmin || isCaptain;

  return (
    <div className="container">
      <div className="team-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '20px' }}>
          {team.logo && (
            <img src={team.logo} alt={team.name} style={{ width: '80px', height: '80px', borderRadius: '12px' }} />
          )}
          <div>
            <h1>{team.name}</h1>
            <p style={{ color: '#757575' }}>{team.teamNumber}</p>
            <span className={`badge ${team.status === 'approved' ? 'badge-success' : team.status === 'pending' ? 'badge-warning' : 'badge-danger'}`}>
              {team.status}
            </span>
          </div>
        </div>
      </div>

      <div className="tabs">
        <button className={activeTab === 'overview' ? 'tab active' : 'tab'} onClick={() => setActiveTab('overview')}>
          Overview
        </button>
        <button className={activeTab === 'roster' ? 'tab active' : 'tab'} onClick={() => setActiveTab('roster')}>
          Roster
        </button>
        <button className={activeTab === 'announcements' ? 'tab active' : 'tab'} onClick={() => setActiveTab('announcements')}>
          Announcements
        </button>
        <button className={activeTab === 'messages' ? 'tab active' : 'tab'} onClick={() => setActiveTab('messages')}>
          Messages
        </button>
        <button className={activeTab === 'training' ? 'tab active' : 'tab'} onClick={() => setActiveTab('training')}>
          Training
        </button>
        <button className={activeTab === 'invitations' ? 'tab active' : 'tab'} onClick={() => setActiveTab('invitations')}>
          Invitations
        </button>
        <button className={activeTab === 'payments' ? 'tab active' : 'tab'} onClick={() => setActiveTab('payments')}>
          Payments
        </button>
        <button className={activeTab === 'guest-history' ? 'tab active' : 'tab'} onClick={() => setActiveTab('guest-history')}>
          Guest History
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'overview' && (
          <>
          <div className="card">
            <h2>Team Information</h2>
            <p><strong>Manager:</strong> {team.manager ? `${team.manager.firstName} ${team.manager.lastName}` : 'N/A'}</p>
            <p><strong>Captain:</strong> {team.captain ? `${team.captain.firstName} ${team.captain.lastName}` : 'N/A'}</p>
            <p><strong>Status:</strong> <span className={`badge ${team.status === 'approved' ? 'badge-success' : 'badge-warning'}`}>{team.status}</span></p>
            <div style={{ marginTop: '20px' }}>
              <h3>Team Colors</h3>
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <div style={{ width: '50px', height: '50px', backgroundColor: team.colors.primary, border: '1px solid #E0E0E0', borderRadius: '8px' }} />
                <div style={{ width: '50px', height: '50px', backgroundColor: team.colors.secondary, border: '1px solid #E0E0E0', borderRadius: '8px' }} />
              </div>
            </div>
          </div>

          {performanceData ? (
            <>
              <div className="card" style={{ marginTop: '20px' }}>
                <h2>Team Statistics</h2>
                {performanceData.teamStatistics && performanceData.teamStatistics.length > 0 ? (
                  <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                    {performanceData.teamStatistics.map((stat: any, idx: number) => (
                      <div key={idx} style={{ padding: '15px', background: '#F5F5F5', borderRadius: '8px' }}>
                        <h3 style={{ marginBottom: '10px' }}>League Statistics</h3>
                        <p><strong>Wins:</strong> {stat.wins}</p>
                        <p><strong>Losses:</strong> {stat.losses}</p>
                        <p><strong>Draws:</strong> {stat.draws}</p>
                        <p><strong>Goals For:</strong> {stat.goalsFor}</p>
                        <p><strong>Goals Against:</strong> {stat.goalsAgainst}</p>
                        <p><strong>Goal Difference:</strong> {stat.goalsFor - stat.goalsAgainst > 0 ? '+' : ''}{stat.goalsFor - stat.goalsAgainst}</p>
                        <p><strong>Points:</strong> {stat.wins * 3 + stat.draws}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No team statistics available yet.</p>
                )}
                {performanceData.totalMatches !== undefined && (
                  <div style={{ marginTop: '20px', padding: '15px', background: '#E8F5E9', borderRadius: '8px' }}>
                    <p><strong>Total Matches Played:</strong> {performanceData.totalMatches}</p>
                    <p><strong>Win Rate:</strong> {performanceData.winRate}%</p>
                  </div>
                )}
              </div>

              {performanceData.topScorers && performanceData.topScorers.length > 0 && (
                <div className="card" style={{ marginTop: '20px' }}>
                  <h2>Top Scorers</h2>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Player</th>
                        <th>Goals</th>
                        <th>Assists</th>
                        <th>Matches</th>
                      </tr>
                    </thead>
                    <tbody>
                      {performanceData.topScorers.map((scorer: any, idx: number) => (
                        <tr key={idx}>
                          <td>
                            {scorer.player
                              ? `${scorer.player.firstName} ${scorer.player.lastName}`
                              : 'Unknown Player'}
                          </td>
                          <td><strong>{scorer.goals}</strong></td>
                          <td>{scorer.assists}</td>
                          <td>{scorer.matchesPlayed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {performanceData.recentMatches && performanceData.recentMatches.length > 0 && (
                <div className="card" style={{ marginTop: '20px' }}>
                  <h2>Recent Matches</h2>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Opponent</th>
                        <th>Score</th>
                        <th>Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {performanceData.recentMatches.map((match: any) => {
                        const isHome = match.homeTeamId === id;
                        const opponent = isHome ? match.awayTeam : match.homeTeam;
                        const ourScore = isHome ? match.homeScore : match.awayScore;
                        const theirScore = isHome ? match.awayScore : match.homeScore;
                        const result = ourScore > theirScore ? 'W' : ourScore < theirScore ? 'L' : 'D';

                        return (
                          <tr key={match.id}>
                            <td>{new Date(match.scheduledDate).toLocaleDateString()}</td>
                            <td>{opponent?.name || 'Unknown'}</td>
                            <td><strong>{ourScore} - {theirScore}</strong></td>
                            <td>
                              <span className={`badge ${result === 'W' ? 'badge-success' : result === 'L' ? 'badge-danger' : 'badge-warning'}`}>
                                {result === 'W' ? 'Win' : result === 'L' ? 'Loss' : 'Draw'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : null}
          </>
        )}

        {activeTab === 'roster' && (
          <div>
            <div className="card" style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>Team Roster ({rosterMembers.length} players)</h2>
                <div>
                  {canManage && (
                    <>
                      <button className="btn btn-primary" onClick={() => navigate('/players')} style={{ marginRight: '10px' }}>
                        Add Player from Directory
                      </button>
                      <button className="btn btn-secondary" onClick={() => setShowGuestPlayerForm(!showGuestPlayerForm)} style={{ marginRight: '10px' }}>
                        {showGuestPlayerForm ? 'Cancel' : 'Add Guest Player'}
                      </button>
                    </>
                  )}
                  <button className="btn btn-secondary" onClick={() => navigate(`/teams/${id}`)}>
                    View Full Team Details
                  </button>
                </div>
              </div>
            </div>

            {showGuestPlayerForm && canManage && (
              <div className="card" style={{ marginBottom: '20px', background: '#F5F5F5' }}>
                <h3>Add Guest Player for Match</h3>
                <form onSubmit={handleAddGuestPlayer}>
                  <div className="form-group">
                    <label>Player *</label>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowPlayerSearch(true)}
                      >
                        {selectedGuestPlayerName ? 'Change Player' : 'Search Player'}
                      </button>
                      {selectedGuestPlayerName && (
                        <span style={{ fontWeight: '500' }}>{selectedGuestPlayerName}</span>
                      )}
                    </div>
                    <input type="hidden" value={guestPlayerForm.userId} required />
                  </div>
                  <div className="form-group">
                    <label>Match *</label>
                    <select
                      value={guestPlayerForm.matchId}
                      onChange={(e) => setGuestPlayerForm({ ...guestPlayerForm, matchId: e.target.value })}
                      required
                    >
                      <option value="">Select a match</option>
                      {matches.filter(m => m.status === 'scheduled' || m.status === 'in_progress').map(match => (
                        <option key={match.id} value={match.id}>
                          {new Date(match.scheduledDate).toLocaleDateString()} - {match.homeTeam?.name} vs {match.awayTeam?.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div className="form-group">
                      <label>Position</label>
                      <select
                        value={guestPlayerForm.position}
                        onChange={(e) => setGuestPlayerForm({ ...guestPlayerForm, position: e.target.value })}
                      >
                        <option value="">Select Position</option>
                        <option value="Goalkeeper">Goalkeeper</option>
                        <option value="Defender">Defender</option>
                        <option value="Midfielder">Midfielder</option>
                        <option value="Forward">Forward</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Jersey Number</label>
                      <input
                        type="number"
                        value={guestPlayerForm.jerseyNumber}
                        onChange={(e) => setGuestPlayerForm({ ...guestPlayerForm, jerseyNumber: e.target.value })}
                        min="1"
                        max="99"
                      />
                    </div>
                  </div>
                  <button type="submit" className="btn btn-primary">Add Guest Player</button>
                </form>
              </div>
            )}

            <div className="card">
              {rosterMembers.length === 0 ? (
                <p>No players on this team yet. Use the "Add Player from Directory" button to add players.</p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Photo</th>
                      <th>Jersey #</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Position</th>
                      <th>Role</th>
                      <th>Type</th>
                      {canManage && <th>Active</th>}
                      <th>Status</th>
                      <th>Discipline</th>
                      {canManage && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rosterMembers.map((member) => {
                      const status = playerStatuses[member.id];
                      const canPlay = status?.canPlay ?? true;
                      const statusReasons = status?.statusReasons || [];
                      
                      return (
                        <tr key={member.id}>
                          <td>
                            {member.user ? (
                              <img 
                                src={getProfilePicture(member.user)} 
                                alt={`${member.user.firstName} ${member.user.lastName}`}
                                style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                              />
                            ) : (
                              <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#E0E0E0' }} />
                            )}
                          </td>
                          <td>{member.jerseyNumber || '-'}</td>
                          <td>
                            {member.user ? (
                              <Link to={`/users/${member.user.id}`} style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}>
                                {member.user.firstName} {member.user.lastName}
                              </Link>
                            ) : (
                              'Unknown'
                            )}
                          </td>
                          <td>{member.user?.email || '-'}</td>
                          <td>
                            {canManage ? (
                              <select
                                value={member.position || ''}
                                onChange={(e) => handleUpdateRole(member.id, member.role, e.target.value)}
                                style={{ padding: '5px', border: '1px solid #E0E0E0', borderRadius: '8px' }}
                              >
                                <option value="">Select Position</option>
                                <option value="Goalkeeper">Goalkeeper</option>
                                <option value="Defender">Defender</option>
                                <option value="Midfielder">Midfielder</option>
                                <option value="Forward">Forward</option>
                              </select>
                            ) : (
                              member.position || '-'
                            )}
                          </td>
                          <td>
                            {canManage ? (
                              <select
                                value={member.role}
                                onChange={(e) => handleUpdateRole(member.id, e.target.value, member.position)}
                                style={{ padding: '5px', border: '1px solid #E0E0E0', borderRadius: '8px' }}
                              >
                                <option value="player">Player</option>
                                <option value="captain">Captain</option>
                                <option value="coach">Coach</option>
                                <option value="manager">Manager</option>
                              </select>
                            ) : (
                              <span className={`badge ${member.role === 'captain' ? 'badge-info' : 'badge-secondary'}`}>
                                {member.role}
                              </span>
                            )}
                          </td>
                          <td>
                            {member.isGuest ? (
                              <span className="badge badge-warning">Guest</span>
                            ) : (
                              <span className="badge badge-success">Regular</span>
                            )}
                          </td>
                          {canManage && (
                            <td>
                              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={member.isActive}
                                  onChange={() => handleToggleActive(member.id, member.isActive)}
                                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                />
                                <span style={{ marginLeft: '8px', fontSize: '14px' }}>
                                  {member.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </label>
                            </td>
                          )}
                          <td>
                            {status ? (
                              <div>
                                <span className={`badge ${canPlay ? 'badge-success' : 'badge-danger'}`}>
                                  {canPlay ? 'Eligible' : 'Ineligible'}
                                </span>
                                {statusReasons.length > 0 && (
                                  <div style={{ fontSize: '11px', color: '#757575', marginTop: '4px' }}>
                                    {statusReasons.join(', ')}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <LoadingSpinner size="small" inline />
                            )}
                          </td>
                          <td>
                            {status ? (
                              <div style={{ fontSize: '14px' }}>
                                {status.yellowCards > 0 && (
                                  <span className="badge badge-warning" style={{ marginRight: '5px' }}>
                                    🟨 {status.yellowCards}
                                  </span>
                                )}
                                {status.redCards > 0 && (
                                  <span className="badge badge-danger">
                                    🟥 {status.redCards}
                                  </span>
                                )}
                                {status.yellowCards === 0 && status.redCards === 0 && (
                                  <span style={{ color: '#9E9E9E' }}>No cards</span>
                                )}
                              </div>
                            ) : (
                              <span style={{ color: '#9E9E9E' }}>-</span>
                            )}
                          </td>
                          {canManage && (
                            <td>
                              <button
                                className="btn btn-danger"
                                style={{ fontSize: '12px', padding: '5px 10px' }}
                                onClick={() => handleRemoveMember(member.id)}
                              >
                                Remove
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {activeTab === 'announcements' && (
          <div>
            {canManage && (
              <div className="card" style={{ marginBottom: '20px' }}>
                <h3>Create Announcement</h3>
                <form onSubmit={handleCreateAnnouncement}>
                  <div className="form-group">
                    <label>Title *</label>
                    <input
                      type="text"
                      value={newAnnouncement.title}
                      onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Content *</label>
                    <textarea
                      value={newAnnouncement.content}
                      onChange={(e) => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })}
                      required
                      rows={4}
                      style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: '8px' }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Priority</label>
                    <select
                      value={newAnnouncement.priority}
                      onChange={(e) => setNewAnnouncement({ ...newAnnouncement, priority: e.target.value })}
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <button type="submit" className="btn btn-primary">Create Announcement</button>
                </form>
              </div>
            )}

            <div className="card">
              <h2>Announcements</h2>
              {announcements.length === 0 ? (
                <p>No announcements yet.</p>
              ) : (
                announcements.map((ann) => (
                  <div key={ann.id} className="announcement-card" style={{ marginBottom: '15px', padding: '15px', border: '1px solid #E0E0E0', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <h4>{ann.title}</h4>
                      <span className={`badge ${ann.priority === 'high' ? 'badge-danger' : ann.priority === 'low' ? 'badge-info' : 'badge-warning'}`}>
                        {ann.priority}
                      </span>
                    </div>
                    <p>{ann.content}</p>
                    <p style={{ fontSize: '12px', color: '#757575', marginTop: '10px' }}>
                      By {ann.author ? `${ann.author.firstName} ${ann.author.lastName}` : 'Unknown'} • {new Date(ann.createdAt).toLocaleString()}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'messages' && (
          <div>
            <div className="card" style={{ marginBottom: '20px' }}>
              <h3>Send Message</h3>
              <form onSubmit={handleSendMessage}>
                <div className="form-group">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    rows={3}
                    style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: '8px' }}
                  />
                </div>
                <button type="submit" className="btn btn-primary">Send Message</button>
              </form>
            </div>

            <div className="card">
              <h2>Team Messages</h2>
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {messages.length === 0 ? (
                  <p>No messages yet.</p>
                ) : (
                  messages.map((msg) => (
                    <div key={msg.id} style={{ marginBottom: '15px', padding: '10px', background: '#F5F5F5', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                        <strong>{msg.sender ? `${msg.sender.firstName} ${msg.sender.lastName}` : 'Unknown'}</strong>
                        <span style={{ fontSize: '12px', color: '#757575' }}>{new Date(msg.createdAt).toLocaleString()}</span>
                      </div>
                      <p style={{ margin: 0 }}>{msg.content}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'training' && (
          <div>
            {canManage && (
              <div className="card" style={{ marginBottom: '20px' }}>
                <h3>Schedule Training Session</h3>
                <form onSubmit={handleCreateTraining}>
                  <div className="form-group">
                    <label>Title *</label>
                    <input
                      type="text"
                      value={newTraining.title}
                      onChange={(e) => setNewTraining({ ...newTraining, title: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={newTraining.description}
                      onChange={(e) => setNewTraining({ ...newTraining, description: e.target.value })}
                      rows={3}
                      style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: '8px' }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div className="form-group">
                      <label>Date *</label>
                      <input
                        type="date"
                        value={newTraining.scheduledDate}
                        onChange={(e) => setNewTraining({ ...newTraining, scheduledDate: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Time *</label>
                      <input
                        type="time"
                        value={newTraining.scheduledTime}
                        onChange={(e) => setNewTraining({ ...newTraining, scheduledTime: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Location *</label>
                    <input
                      type="text"
                      value={newTraining.location}
                      onChange={(e) => setNewTraining({ ...newTraining, location: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Duration (minutes)</label>
                    <input
                      type="number"
                      value={newTraining.duration}
                      onChange={(e) => setNewTraining({ ...newTraining, duration: e.target.value })}
                    />
                  </div>
                  <button type="submit" className="btn btn-primary">Schedule Training</button>
                </form>
              </div>
            )}

            <div className="card">
              <h2>Training Sessions</h2>
              {trainingSessions.length === 0 ? (
                <p>No training sessions scheduled.</p>
              ) : (
                trainingSessions.map((session) => (
                  <div key={session.id} className="training-card" style={{ marginBottom: '15px', padding: '15px', border: '1px solid #E0E0E0', borderRadius: '8px' }}>
                    <h4>{session.title}</h4>
                    {session.description && <p>{session.description}</p>}
                    <p><strong>Date:</strong> {new Date(session.scheduledDate).toLocaleDateString()} at {session.scheduledTime}</p>
                    <p><strong>Location:</strong> {session.location}</p>
                    {session.duration && <p><strong>Duration:</strong> {session.duration} minutes</p>}
                    <p><strong>RSVPs:</strong> {session.attendingCount} attending, {session.rsvpCount} total</p>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'payments' && (
          <div>
            {(isAdmin || user?.profileType === 'director') && (
              <div className="card" style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={{ margin: 0 }}>Auto-Generate Invoices</h3>
                    <p style={{ color: '#757575', fontSize: '13px', margin: '5px 0 0' }}>
                      Creates missing league fee, team fee, and multi-team discount invoices for all members
                    </p>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={async () => {
                      try {
                        const res = await api.post(`/payments/team/${id}/generate`);
                        const count = res.data.created || 0;
                        alert(count > 0 ? `${count} invoice(s) created.` : 'All invoices already exist. Nothing to create.');
                        fetchTeamData();
                      } catch (error: any) {
                        alert(error.response?.data?.error || 'Failed to generate invoices');
                      }
                    }}
                  >
                    Generate Invoices
                  </button>
                </div>
              </div>
            )}
            {canManage && (
              <div className="card" style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3>Create Invoice</h3>
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowPaymentForm(!showPaymentForm)}
                  >
                    {showPaymentForm ? 'Cancel' : 'Create Invoice'}
                  </button>
                </div>
                {showPaymentForm && (
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      await api.post(`/payments/team/${id}`, {
                        playerId: paymentForm.playerId,
                        amount: parseFloat(paymentForm.amount),
                        description: paymentForm.description || undefined,
                        dueDate: paymentForm.dueDate || undefined,
                      });
                      setPaymentForm({ playerId: '', amount: '', description: '', dueDate: '' });
                      setShowPaymentForm(false);
                      fetchTeamData();
                    } catch (error: any) {
                      alert(error.response?.data?.error || 'Failed to create invoice');
                    }
                  }}>
                    <div className="form-group">
                      <label>Player</label>
                      <select
                        value={paymentForm.playerId}
                        onChange={(e) => setPaymentForm({ ...paymentForm, playerId: e.target.value })}
                        required
                      >
                        <option value="">Select Player</option>
                        {rosterMembers.filter((m) => !m.isGuest).map((m) => (
                          <option key={m.id} value={m.user?.id}>
                            {m.user?.firstName} {m.user?.lastName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div className="form-group">
                        <label>Amount ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={paymentForm.amount}
                          onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Due Date</label>
                        <input
                          type="date"
                          value={paymentForm.dueDate}
                          onChange={(e) => setPaymentForm({ ...paymentForm, dueDate: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <input
                        type="text"
                        value={paymentForm.description}
                        onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })}
                        placeholder="e.g. Season registration fee"
                      />
                    </div>
                    <button type="submit" className="btn btn-primary">Create Invoice</button>
                  </form>
                )}
              </div>
            )}

            <div className="card">
              <h2>Team Payments</h2>
              {teamPayments.length === 0 ? (
                <p>No payments found.</p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Paid</th>
                      <th>Due Date</th>
                      <th>Status</th>
                      <th>Method</th>
                      {canManage && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {teamPayments.map((p: any) => (
                      <React.Fragment key={p.id}>
                        <tr>
                          <td>{p.player ? `${p.player.firstName} ${p.player.lastName}` : 'N/A'}</td>
                          <td>{p.description || '-'}</td>
                          <td>${p.amount?.toFixed(2)}</td>
                          <td>${p.amountPaid?.toFixed(2)}</td>
                          <td>{p.dueDate || '-'}</td>
                          <td>
                            <span className={`badge ${p.status === 'paid' ? 'badge-success' : p.status === 'partial' ? 'badge-warning' : 'badge-danger'}`}>
                              {p.status}
                            </span>
                          </td>
                          <td>{p.paymentMethod || 'offline'}</td>
                          {canManage && (
                            <td style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                              {p.status !== 'paid' && (
                                <button
                                  className="btn btn-success"
                                  style={{ fontSize: '12px', padding: '5px 10px' }}
                                  onClick={() => {
                                    if (markPaidId === p.id) {
                                      setMarkPaidId(null);
                                    } else {
                                      setMarkPaidId(p.id);
                                      setMarkPaidForm({
                                        amountPaid: String(p.amount - p.amountPaid),
                                        paymentMethod: 'offline',
                                        notes: ''
                                      });
                                    }
                                  }}
                                >
                                  {markPaidId === p.id ? 'Cancel' : 'Mark Paid'}
                                </button>
                              )}
                              <button
                                className="btn btn-danger"
                                style={{ fontSize: '12px', padding: '5px 10px' }}
                                onClick={async () => {
                                  if (window.confirm('Delete this payment?')) {
                                    try {
                                      await api.delete(`/payments/${p.id}`);
                                      fetchTeamData();
                                    } catch (error: any) {
                                      alert(error.response?.data?.error || 'Failed to delete');
                                    }
                                  }
                                }}
                              >
                                Delete
                              </button>
                            </td>
                          )}
                        </tr>
                        {markPaidId === p.id && (
                          <tr>
                            <td colSpan={canManage ? 8 : 7} style={{ background: '#F5F5F5', padding: '15px' }}>
                              <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                <div className="form-group" style={{ margin: 0 }}>
                                  <label style={{ fontSize: '13px' }}>Amount ($)</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={markPaidForm.amountPaid}
                                    onChange={(e) => setMarkPaidForm({ ...markPaidForm, amountPaid: e.target.value })}
                                    style={{ padding: '8px', borderRadius: '8px', border: '1px solid #E0E0E0', width: '120px' }}
                                  />
                                </div>
                                <div className="form-group" style={{ margin: 0 }}>
                                  <label style={{ fontSize: '13px' }}>Payment Method</label>
                                  <select
                                    value={markPaidForm.paymentMethod}
                                    onChange={(e) => setMarkPaidForm({ ...markPaidForm, paymentMethod: e.target.value })}
                                    style={{ padding: '8px', borderRadius: '8px', border: '1px solid #E0E0E0' }}
                                  >
                                    <option value="offline">Offline</option>
                                    <option value="cash">Cash</option>
                                    <option value="check">Check</option>
                                    <option value="venmo">Venmo</option>
                                    <option value="stripe">Stripe</option>
                                    <option value="other">Other</option>
                                  </select>
                                </div>
                                <div className="form-group" style={{ margin: 0, flex: 1, minWidth: '150px' }}>
                                  <label style={{ fontSize: '13px' }}>Notes</label>
                                  <input
                                    type="text"
                                    value={markPaidForm.notes}
                                    onChange={(e) => setMarkPaidForm({ ...markPaidForm, notes: e.target.value })}
                                    placeholder="Optional notes"
                                    style={{ padding: '8px', borderRadius: '8px', border: '1px solid #E0E0E0', width: '100%' }}
                                  />
                                </div>
                                <button
                                  className="btn btn-primary"
                                  style={{ fontSize: '13px', padding: '8px 16px' }}
                                  onClick={async () => {
                                    try {
                                      const paidAmount = parseFloat(markPaidForm.amountPaid) + p.amountPaid;
                                      await api.patch(`/payments/${p.id}/mark-paid`, {
                                        amountPaid: paidAmount,
                                        paymentMethod: markPaidForm.paymentMethod,
                                        notes: markPaidForm.notes || undefined
                                      });
                                      setMarkPaidId(null);
                                      fetchTeamData();
                                    } catch (error: any) {
                                      alert(error.response?.data?.error || 'Failed to mark as paid');
                                    }
                                  }}
                                >
                                  Confirm Payment
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {activeTab === 'invitations' && (
          <div>
            {canManage && (
              <div className="card" style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3>Create Invitation Link</h3>
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowInvitationForm(!showInvitationForm)}
                  >
                    {showInvitationForm ? 'Cancel' : 'Create New Invitation'}
                  </button>
                </div>

                {showInvitationForm && (
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      const response = await api.post(`/invitations/teams/${id}/invitations`, {
                        email: invitationForm.email || undefined,
                        role: invitationForm.role,
                        position: invitationForm.position || undefined,
                        expiresInDays: parseInt(invitationForm.expiresInDays) || 30,
                        maxUses: invitationForm.maxUses ? parseInt(invitationForm.maxUses) : undefined
                      });
                      alert(`Invitation created! Share this link:\n${response.data.invitationLink}`);
                      setInvitationForm({ email: '', role: 'player', position: '', expiresInDays: '30', maxUses: '' });
                      setShowInvitationForm(false);
                      fetchTeamData();
                    } catch (error: any) {
                      console.error('Invitation creation error:', error);
                      alert(error.response?.data?.error || 'Failed to create invitation');
                    }
                  }}>
                    <div className="form-group">
                      <label>Email (optional)</label>
                      <input
                        type="email"
                        value={invitationForm.email}
                        onChange={(e) => setInvitationForm({ ...invitationForm, email: e.target.value })}
                        placeholder="Enter email for targeted invitation (optional)"
                      />
                      <small style={{ color: '#757575', fontSize: '12px' }}>Leave empty for a general invitation link</small>
                    </div>
                    <div className="form-group">
                      <label>Default Role</label>
                      <select
                        value={invitationForm.role}
                        onChange={(e) => setInvitationForm({ ...invitationForm, role: e.target.value })}
                      >
                        <option value="player">Player</option>
                        <option value="captain">Captain</option>
                        <option value="coach">Coach</option>
                        <option value="manager">Manager</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Default Position (optional)</label>
                      <select
                        value={invitationForm.position}
                        onChange={(e) => setInvitationForm({ ...invitationForm, position: e.target.value })}
                      >
                        <option value="">No default position</option>
                        <option value="Goalkeeper">Goalkeeper</option>
                        <option value="Defender">Defender</option>
                        <option value="Midfielder">Midfielder</option>
                        <option value="Forward">Forward</option>
                      </select>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div className="form-group">
                        <label>Expires In (days)</label>
                        <input
                          type="number"
                          value={invitationForm.expiresInDays}
                          onChange={(e) => setInvitationForm({ ...invitationForm, expiresInDays: e.target.value })}
                          min="1"
                          max="365"
                        />
                      </div>
                      <div className="form-group">
                        <label>Max Uses (optional)</label>
                        <input
                          type="number"
                          value={invitationForm.maxUses}
                          onChange={(e) => setInvitationForm({ ...invitationForm, maxUses: e.target.value })}
                          min="1"
                          placeholder="Unlimited if empty"
                        />
                      </div>
                    </div>
                    <button type="submit" className="btn btn-primary">Create Invitation</button>
                  </form>
                )}
              </div>
            )}

            <div className="card">
              <h2>Active Invitations</h2>
              {invitations.length === 0 ? (
                <p>No invitations created yet.</p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Invitation Link</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Position</th>
                      <th>Uses</th>
                      <th>Expires</th>
                      <th>Status</th>
                      {canManage && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {invitations.map((inv) => (
                      <tr key={inv.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input
                              type="text"
                              value={inv.invitationLink}
                              readOnly
                              style={{ flex: 1, padding: '5px', fontSize: '12px', fontFamily: 'monospace' }}
                              onClick={(e) => (e.target as HTMLInputElement).select()}
                            />
                            <button
                              className="btn btn-secondary"
                              style={{ fontSize: '12px', padding: '5px 10px' }}
                              onClick={() => {
                                navigator.clipboard.writeText(inv.invitationLink);
                                alert('Link copied to clipboard!');
                              }}
                            >
                              Copy
                            </button>
                          </div>
                        </td>
                        <td>{inv.email || '-'}</td>
                        <td><span className="badge badge-info">{inv.role}</span></td>
                        <td>{inv.position || '-'}</td>
                        <td>
                          {inv.maxUses ? `${inv.currentUses}/${inv.maxUses}` : inv.currentUses}
                        </td>
                        <td>{new Date(inv.expiresAt).toLocaleDateString()}</td>
                        <td>
                          {inv.isExpired ? (
                            <span className="badge badge-danger">Expired</span>
                          ) : inv.isMaxedOut ? (
                            <span className="badge badge-warning">Max Uses</span>
                          ) : inv.isActive ? (
                            <span className="badge badge-success">Active</span>
                          ) : (
                            <span className="badge badge-secondary">Inactive</span>
                          )}
                        </td>
                        {canManage && (
                          <td>
                            {inv.isActive && (
                              <button
                                className="btn btn-danger"
                                style={{ fontSize: '12px', padding: '5px 10px' }}
                                onClick={async () => {
                                  if (window.confirm('Deactivate this invitation?')) {
                                    try {
                                      await api.patch(`/invitations/${inv.token}/deactivate`);
                                      fetchTeamData();
                                    } catch (error: any) {
                                      alert(error.response?.data?.error || 'Failed to deactivate');
                                    }
                                  }
                                }}
                              >
                                Deactivate
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Guest Player Invites Section */}
            {canManage && (
              <div className="card" style={{ marginTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3>Guest Player Invites</h3>
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowGuestInviteForm(!showGuestInviteForm)}
                  >
                    {showGuestInviteForm ? 'Cancel' : 'Create Guest Invite'}
                  </button>
                </div>

                {showGuestInviteForm && (
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    if (guestInviteForm.matchIds.length === 0) {
                      alert('Please select at least one match');
                      return;
                    }
                    try {
                      const response = await api.post(`/guest-invites/teams/${id}/invites`, {
                        email: guestInviteForm.email || undefined,
                        matchIds: guestInviteForm.matchIds,
                        position: guestInviteForm.position || undefined,
                        jerseyNumber: guestInviteForm.jerseyNumber ? parseInt(guestInviteForm.jerseyNumber) : undefined,
                        expiresInDays: parseInt(guestInviteForm.expiresInDays) || 30,
                        maxUses: parseInt(guestInviteForm.maxUses) || 1
                      });
                      alert(`Guest invite created! Share this link:\n${response.data.inviteLink}`);
                      setGuestInviteForm({ email: '', matchIds: [], position: '', jerseyNumber: '', expiresInDays: '30', maxUses: '1' });
                      setShowGuestInviteForm(false);
                      fetchTeamData();
                    } catch (error: any) {
                      alert(error.response?.data?.error || 'Failed to create guest invite');
                    }
                  }} style={{ marginBottom: '20px', padding: '15px', background: '#F5F5F5', borderRadius: '8px' }}>
                    <div className="form-group">
                      <label>Matches *</label>
                      <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #E0E0E0', borderRadius: '8px', padding: '10px', background: '#fff' }}>
                        {matches.filter(m => m.status === 'scheduled' || m.status === 'in_progress').length === 0 ? (
                          <p style={{ color: '#757575', margin: 0 }}>No upcoming matches found</p>
                        ) : (
                          matches.filter(m => m.status === 'scheduled' || m.status === 'in_progress').map(match => (
                            <label key={match.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 0', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={guestInviteForm.matchIds.indexOf(match.id) > -1}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setGuestInviteForm({ ...guestInviteForm, matchIds: [...guestInviteForm.matchIds, match.id] });
                                  } else {
                                    setGuestInviteForm({ ...guestInviteForm, matchIds: guestInviteForm.matchIds.filter((mid: string) => mid !== match.id) });
                                  }
                                }}
                                style={{ width: '18px', height: '18px' }}
                              />
                              <span>{new Date(match.scheduledDate).toLocaleDateString()} - {match.homeTeam?.name} vs {match.awayTeam?.name}</span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Email (optional)</label>
                      <input
                        type="email"
                        value={guestInviteForm.email}
                        onChange={(e) => setGuestInviteForm({ ...guestInviteForm, email: e.target.value })}
                        placeholder="Target email (optional)"
                      />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div className="form-group">
                        <label>Position</label>
                        <select
                          value={guestInviteForm.position}
                          onChange={(e) => setGuestInviteForm({ ...guestInviteForm, position: e.target.value })}
                        >
                          <option value="">No position</option>
                          <option value="Goalkeeper">Goalkeeper</option>
                          <option value="Defender">Defender</option>
                          <option value="Midfielder">Midfielder</option>
                          <option value="Forward">Forward</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Jersey Number</label>
                        <input
                          type="number"
                          value={guestInviteForm.jerseyNumber}
                          onChange={(e) => setGuestInviteForm({ ...guestInviteForm, jerseyNumber: e.target.value })}
                          min="1"
                          max="99"
                        />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div className="form-group">
                        <label>Expires In (days)</label>
                        <input
                          type="number"
                          value={guestInviteForm.expiresInDays}
                          onChange={(e) => setGuestInviteForm({ ...guestInviteForm, expiresInDays: e.target.value })}
                          min="1"
                          max="365"
                        />
                      </div>
                      <div className="form-group">
                        <label>Max Uses</label>
                        <input
                          type="number"
                          value={guestInviteForm.maxUses}
                          onChange={(e) => setGuestInviteForm({ ...guestInviteForm, maxUses: e.target.value })}
                          min="1"
                        />
                      </div>
                    </div>
                    <button type="submit" className="btn btn-primary">Create Guest Invite</button>
                  </form>
                )}

                {guestInvites.length === 0 ? (
                  <p>No guest invites created yet.</p>
                ) : (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Link</th>
                        <th>Email</th>
                        <th>Matches</th>
                        <th>Uses</th>
                        <th>Expires</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {guestInvites.map((inv: any) => (
                        <tr key={inv.id}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <button
                                className="btn btn-secondary"
                                style={{ fontSize: '12px', padding: '5px 10px' }}
                                onClick={() => {
                                  navigator.clipboard.writeText(inv.inviteLink);
                                  alert('Link copied!');
                                }}
                              >
                                Copy Link
                              </button>
                            </div>
                          </td>
                          <td>{inv.email || '-'}</td>
                          <td>{inv.matchIds?.length || 0} match(es)</td>
                          <td>{inv.currentUses}/{inv.maxUses}</td>
                          <td>{new Date(inv.expiresAt).toLocaleDateString()}</td>
                          <td>
                            {inv.isExpired ? (
                              <span className="badge badge-danger">Expired</span>
                            ) : inv.isMaxedOut ? (
                              <span className="badge badge-warning">Max Uses</span>
                            ) : inv.isActive ? (
                              <span className="badge badge-success">Active</span>
                            ) : (
                              <span className="badge badge-secondary">Inactive</span>
                            )}
                          </td>
                          <td>
                            {inv.isActive && (
                              <button
                                className="btn btn-danger"
                                style={{ fontSize: '12px', padding: '5px 10px' }}
                                onClick={async () => {
                                  if (window.confirm('Deactivate this guest invite?')) {
                                    try {
                                      await api.patch(`/guest-invites/${inv.token}/deactivate`);
                                      fetchTeamData();
                                    } catch (error: any) {
                                      alert(error.response?.data?.error || 'Failed to deactivate');
                                    }
                                  }
                                }}
                              >
                                Deactivate
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'guest-history' && (
          <div>
            <div className="card">
              <h2>Guest Player History</h2>
              {guestHistory.length === 0 ? (
                <p>No guest player history yet.</p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Action</th>
                      <th>Match</th>
                      <th>Invited By</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guestHistory.map((entry: any) => (
                      <tr key={entry.id}>
                        <td>
                          {entry.player ? `${entry.player.firstName} ${entry.player.lastName}` : 'Unknown'}
                        </td>
                        <td>
                          <span className={`badge ${
                            entry.action === 'added' ? 'badge-success' :
                            entry.action === 'played' ? 'badge-info' :
                            entry.action === 'removed' ? 'badge-danger' :
                            entry.action === 'limit_reached' ? 'badge-warning' :
                            'badge-secondary'
                          }`}>
                            {entry.action === 'limit_reached' ? 'Limit Reached' : entry.action}
                          </span>
                        </td>
                        <td>
                          {entry.match ? (
                            <span>
                              {new Date(entry.match.scheduledDate).toLocaleDateString()}
                              {entry.match.homeTeam && entry.match.awayTeam && (
                                <span style={{ fontSize: '12px', color: '#757575', display: 'block' }}>
                                  {entry.match.homeTeam.name} vs {entry.match.awayTeam.name}
                                </span>
                              )}
                            </span>
                          ) : '-'}
                        </td>
                        <td>
                          {entry.invitedByUser ? `${entry.invitedByUser.firstName} ${entry.invitedByUser.lastName}` : '-'}
                        </td>
                        <td>{new Date(entry.createdAt).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>

      <PlayerSearchDialog
        isOpen={showPlayerSearch}
        onClose={() => setShowPlayerSearch(false)}
        onSelect={(player) => {
          setGuestPlayerForm({ ...guestPlayerForm, userId: player.id });
          setSelectedGuestPlayerName(`${player.firstName} ${player.lastName}`);
        }}
      />
    </div>
  );
};

export default TeamManagement;

